import { useState } from "react";
import { Bike, ChevronDown, Footprints, TramFront } from "lucide-react";

import { cn } from "@/design-system/cn.ts";
import { Hint } from "@/design-system/Hint.tsx";
import { legsLabel, legsSummary, MODE_LABELS, travelTime } from "@/travel/travel.format.ts";
import { travelRows, type TravelMode } from "@/travel/travel.model.ts";
import type { Anchor, Travel } from "@housing/shared";

/**
 * How long it takes to get to work, by the ways you'd actually consider.
 *
 * Deliberately not an itinerary. The old Discord card led with a five-segment
 * chain — "6m walk → 13m bus(38R) → 4m walk → 5m bus(8) → 3m walk" — which
 * answers "how would I get there" when the question being asked is "how long
 * does it take". The chain is still here, one tap down, for the reader deciding
 * whether two transfers is tolerable.
 */

const MODE_ICONS: Record<TravelMode, React.ComponentType<{ className?: string }>> = {
  walk: Footprints,
  // lucide has no scooter or moped; Bike is the only two-wheeler, and the row
  // label carries the actual meaning.
  scooter: Bike,
  transit: TramFront,
};

interface TravelPanelProps {
  travel: Travel;
  /** Where these times are measured to, when the server knows. */
  anchor: Anchor | null;
}

export function TravelPanel({ travel, anchor }: TravelPanelProps) {
  const [openLegs, setOpenLegs] = useState(false);
  const rows = travelRows(travel);

  // Nothing priced for this unit — show no block rather than a row of dashes.
  // "Unknown" costs a line and tells the reader nothing they can act on.
  if (rows.length === 0) return null;

  // Only a configured HOUSING_ANCHOR_LABEL earns the destination line. With
  // coordinates but no name there is nothing readable to print — "to the anchor"
  // is our word for it, not the reader's — so the label is dropped and the
  // coordinates move into the hint on the heading instead.
  const destination = anchor?.label ?? null;

  return (
    <div className="space-y-1.5 border-t border-rule pt-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <Hint
          label={destination ? `Times are to ${destination}.` : "Times are to the office."}
          detail={
            anchor
              ? `Modelled for a weekday morning arrival at ${anchor.lat.toFixed(4)}, ${anchor.lon.toFixed(4)}.`
              : "Modelled for a weekday morning arrival."
          }
          side="top"
          align="start"
        >
          <button type="button" className="eyebrow cursor-default">
            travel
          </button>
        </Hint>
        {destination && (
          <span className="eyebrow normal-case tracking-normal">to {destination}</span>
        )}
      </div>

      <ul className="space-y-1">
        {rows.map((row) => {
          const Icon = MODE_ICONS[row.mode];
          // A one-leg itinerary has nothing to disclose — expanding "1 leg" just
          // restates the row. Only a genuine chain gets the control.
          const expandable = row.mode === "transit" && row.legs.length > 1;

          return (
            <li key={row.mode}>
              <div className="flex items-center gap-2 text-[12.5px]">
                <span className="text-chalk-mute">
                  <Icon className="size-3.5" />
                </span>
                <span className={cn(row.fastest ? "text-chalk" : "text-chalk-dim")}>
                  {MODE_LABELS[row.mode]}
                </span>
                <span
                  className={cn(
                    "tnum ml-auto",
                    row.fastest ? "font-medium text-chalk" : "text-chalk-dim",
                  )}
                >
                  {travelTime(row.mins)}
                </span>
              </div>

              {row.mode === "transit" && (
                <div className="flex items-center gap-1.5 pl-[22px] pt-0.5">
                  {row.savesMin != null && (
                    <span className="eyebrow tracking-[0.1em] text-signal">
                      ~{row.savesMin} min faster
                    </span>
                  )}
                  {expandable && (
                    <button
                      type="button"
                      onClick={() => setOpenLegs((o) => !o)}
                      aria-expanded={openLegs}
                      className="flex items-center gap-0.5 text-[10.5px] text-chalk-mute transition hover:text-chalk-dim"
                    >
                      {legsSummary(row.legs)}
                      <ChevronDown
                        className={cn("size-3 transition-transform", openLegs && "rotate-180")}
                      />
                    </button>
                  )}
                </div>
              )}

              {expandable && openLegs && (
                <p className="pl-[22px] pt-1 text-[11px] leading-snug text-chalk-dim">
                  {legsLabel(row.legs)}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
